/**
 * Title lookup for references that carry no usable identifier — the core of the fake
 * (AI-hallucinated) reference check.
 *
 * A reference without a DOI, PMID, ISBN or arXiv ID cannot be resolved directly, so the
 * browser searches Crossref and then OpenAlex for its title and judges whether the best
 * hit is the same work. Both APIs answer browsers directly, so the reference never
 * passes through CitationEasy's server. Only the picked-out title, first author and
 * year are sent (or the reference line itself when no title can be picked out).
 *
 * The verdict is evidence, never proof: "not-found" means neither index holds a
 * matching record, which is what a fabricated reference looks like, but books, reports,
 * web pages and older or regional literature are often missing from both.
 */

const CROSSREF_WORKS = "https://api.crossref.org/works";
const OPENALEX_WORKS = "https://api.openalex.org/works";
/** Names CitationEasy, never the reader, for Crossref's and OpenAlex's polite pools. */
const MAILTO = "support@citationeasy.com";
const CANDIDATE_ROWS = 5;
const MAX_QUERY_LENGTH = 300;

/** A strong title match: the same work, perhaps with a subtitle or typo. */
export const FOUND_THRESHOLD = 0.85;
/** Close enough that the writer should compare the two records by hand. */
export const CLOSE_THRESHOLD = 0.7;
/** Shown as the nearest record on a "not-found" verdict so the reader can judge. */
const NEAREST_THRESHOLD = 0.4;
/** Titles with fewer meaningful words than this need a named author to confirm. */
const SHORT_TITLE_TOKENS = 4;

const QUOTED_TITLE_REGEX = /[“"]([^”"]{8,}?)[,.]?[”"]/;
const DATED_TITLE_REGEX =
  /\((?:(?:18|19|20|21)\d{2}[a-z]?|n\.d\.)[^)]{0,20}\)\.?\s*([^.!?]{8,}[.!?]?)/i;
const SENTENCE_SPLIT_REGEX = /(?<=[.?!])\s+/;
const YEAR_REGEX = /\b(18|19|20)\d{2}\b/;
const PAREN_YEAR_REGEX = /\(((?:18|19|20)\d{2})[a-z]?[^)]{0,20}\)/i;
const URL_REGEX = /\bhttps?:\/\/\S+/gi;
const DOI_URL_REGEX = /^https?:\/\/(?:dx\.)?doi\.org\//i;
const WORD_REGEX = /[\p{L}\p{N}]+/gu;
const DIACRITICS_REGEX = /\p{M}/gu;
const TRAILING_PUNCTUATION_REGEX = /[\s.,;:]+$/;
const FILTER_UNSAFE_REGEX = /[,:|]/g;
const WHITESPACE_REGEX = /\s+/g;
const ET_AL_REGEX = /\bet al\b/i;
const LEADING_DIGIT_REGEX = /^\d/;
const SUBTITLE_SPLIT_REGEX = /[:?]\s/;

const STOP_WORDS = new Set([
  "and",
  "for",
  "from",
  "into",
  "the",
  "with",
  "its",
  "our",
  "their",
  "via",
]);
const AUTHOR_WORDS = new Set(["and", "eds", "ed", "et", "al"]);

export interface ReferenceClues {
  /** The title picked out of the reference, or null when no style rule found one. */
  title: string | null;
  year: number | null;
  /** A non-DOI web address, which suggests a web page these indexes will not hold. */
  webUrl: string | null;
}

export interface TitleCandidate {
  source: "Crossref" | "OpenAlex";
  title: string;
  /** Family names, in author order. */
  authors: string[];
  year: number | null;
  container: string | null;
  doi: string | null;
  url: string | null;
}

export type TitleVerdict = "found" | "close" | "not-found";

export interface TitleMatch {
  verdict: TitleVerdict;
  /** The best candidate — the match, or on "not-found" the nearest record if any. */
  candidate: TitleCandidate | null;
  titleScore: number;
  /** Which written details disagree with `candidate`. */
  differences: ("title" | "year" | "author")[];
}

export type TitleLookupFailure = "rate-limited" | "unavailable" | "network";

export type TitleLookupResult =
  | ({ ok: true; searched: TitleCandidate["source"][] } & TitleMatch)
  | { ok: false; reason: TitleLookupFailure };

type FetchLike = typeof fetch;

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(DIACRITICS_REGEX, "")
    .toLocaleLowerCase("en");
}

function tokens(value: string): Set<string> {
  const words = normalize(value).match(WORD_REGEX) ?? [];
  return new Set(
    words.filter((word) => word.length > 2 && !STOP_WORDS.has(word))
  );
}

function dice(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) {
    return 0;
  }
  let shared = 0;
  for (const token of a) {
    if (b.has(token)) {
      shared += 1;
    }
  }
  return (2 * shared) / (a.size + b.size);
}

/**
 * Similarity of two titles, 0–1. Compares the full titles and each one's main title
 * (before a colon), so a reference that drops a subtitle still matches.
 */
export function titleSimilarity(written: string, candidate: string): number {
  const writtenVariants = [written, written.split(SUBTITLE_SPLIT_REGEX)[0]];
  const candidateVariants = [
    candidate,
    candidate.split(SUBTITLE_SPLIT_REGEX)[0],
  ];
  let best = 0;
  for (const a of writtenVariants) {
    for (const b of candidateVariants) {
      if (a && b) {
        best = Math.max(best, dice(tokens(a), tokens(b)));
      }
    }
  }
  return best;
}

function cleanTitle(value: string): string | null {
  const title = value.trim().replace(TRAILING_PUNCTUATION_REGEX, "");
  return tokens(title).size >= 2 ? title : null;
}

/** A sentence that reads like a title rather than a run of author names or a locator. */
function looksLikeTitle(sentence: string): boolean {
  if (LEADING_DIGIT_REGEX.test(sentence) || ET_AL_REGEX.test(sentence)) {
    return false;
  }
  const words = (normalize(sentence).match(WORD_REGEX) ?? []).filter(
    (word) => word.length > 2 && !AUTHOR_WORDS.has(word)
  );
  return words.length >= 3;
}

/** Pick the title out of a formatted reference in APA, Harvard, MLA, Chicago, IEEE or Vancouver. */
export function extractReferenceTitle(reference: string): string | null {
  const line = reference.replace(URL_REGEX, " ");
  const quoted = QUOTED_TITLE_REGEX.exec(line)?.[1];
  if (quoted) {
    return cleanTitle(quoted);
  }
  const dated = DATED_TITLE_REGEX.exec(line)?.[1];
  if (dated) {
    return cleanTitle(dated);
  }
  const sentences = line.split(SENTENCE_SPLIT_REGEX).slice(1);
  const title = sentences.find(looksLikeTitle);
  return title ? cleanTitle(title) : null;
}

export function extractReferenceClues(reference: string): ReferenceClues {
  const year =
    PAREN_YEAR_REGEX.exec(reference)?.[1] ?? YEAR_REGEX.exec(reference)?.[0];
  const webUrl =
    reference.match(URL_REGEX)?.find((url) => !DOI_URL_REGEX.test(url)) ?? null;
  return {
    title: extractReferenceTitle(reference),
    year: year ? Number(year) : null,
    webUrl,
  };
}

function queryText(reference: string, clues: ReferenceClues): string {
  const text = clues.title
    ? [clues.title, clues.year].filter(Boolean).join(" ")
    : reference.replace(URL_REGEX, " ");
  return text.replace(WHITESPACE_REGEX, " ").trim().slice(0, MAX_QUERY_LENGTH);
}

export function crossrefSearchUrl(
  reference: string,
  clues: ReferenceClues
): string {
  const url = new URL(CROSSREF_WORKS);
  url.searchParams.set("query.bibliographic", queryText(reference, clues));
  url.searchParams.set("rows", String(CANDIDATE_ROWS));
  url.searchParams.set(
    "select",
    "DOI,title,author,issued,container-title,type,URL"
  );
  url.searchParams.set("mailto", MAILTO);
  return url.toString();
}

export function openAlexSearchUrl(
  reference: string,
  clues: ReferenceClues
): string {
  const url = new URL(OPENALEX_WORKS);
  if (clues.title) {
    const title = clues.title.replace(FILTER_UNSAFE_REGEX, " ");
    url.searchParams.set("filter", `title.search:${title}`);
  } else {
    url.searchParams.set("search", queryText(reference, clues));
  }
  url.searchParams.set("per_page", String(CANDIDATE_ROWS));
  url.searchParams.set(
    "select",
    "id,doi,display_name,publication_year,authorships,primary_location"
  );
  url.searchParams.set("mailto", MAILTO);
  return url.toString();
}

interface CrossrefItem {
  DOI?: string;
  URL?: string;
  title?: string[];
  author?: { family?: string; name?: string }[];
  issued?: { "date-parts"?: (number | null)[][] };
  "container-title"?: string[];
}

export function parseCrossrefCandidates(body: unknown): TitleCandidate[] {
  const items = (body as { message?: { items?: CrossrefItem[] } } | null)
    ?.message?.items;
  if (!Array.isArray(items)) {
    return [];
  }
  return items.flatMap((item): TitleCandidate[] => {
    const title = item.title?.[0];
    if (!title) {
      return [];
    }
    const year = item.issued?.["date-parts"]?.[0]?.[0];
    return [
      {
        source: "Crossref",
        title,
        authors: (item.author ?? [])
          .map((author) => author.family ?? author.name ?? "")
          .filter(Boolean),
        year: typeof year === "number" ? year : null,
        container: item["container-title"]?.[0] ?? null,
        doi: item.DOI?.toLowerCase() ?? null,
        url: item.DOI ? `https://doi.org/${item.DOI}` : (item.URL ?? null),
      },
    ];
  });
}

interface OpenAlexItem {
  id?: string;
  doi?: string | null;
  display_name?: string | null;
  publication_year?: number | null;
  authorships?: { author?: { display_name?: string | null } }[];
  primary_location?: { source?: { display_name?: string | null } | null };
}

function familyName(fullName: string): string {
  const words = fullName.trim().split(WHITESPACE_REGEX);
  return words.at(-1) ?? "";
}

export function parseOpenAlexCandidates(body: unknown): TitleCandidate[] {
  const items = (body as { results?: OpenAlexItem[] } | null)?.results;
  if (!Array.isArray(items)) {
    return [];
  }
  return items.flatMap((item): TitleCandidate[] => {
    if (!item.display_name) {
      return [];
    }
    const doi = item.doi?.replace(DOI_URL_REGEX, "").toLowerCase() ?? null;
    return [
      {
        source: "OpenAlex",
        title: item.display_name,
        authors: (item.authorships ?? [])
          .map((authorship) =>
            familyName(authorship.author?.display_name ?? "")
          )
          .filter(Boolean),
        year: item.publication_year ?? null,
        container: item.primary_location?.source?.display_name ?? null,
        doi,
        url: item.doi ?? item.id ?? null,
      },
    ];
  });
}

/** Whether any of the candidate's first three authors is named in the reference. */
function authorNamed(
  reference: string,
  candidate: TitleCandidate
): boolean | null {
  const surnames = candidate.authors
    .slice(0, 3)
    .map((name) => tokens(name))
    .filter((set) => set.size > 0);
  if (surnames.length === 0) {
    return null;
  }
  const written = new Set(normalize(reference).match(WORD_REGEX) ?? []);
  return surnames.some((set) => [...set].every((word) => written.has(word)));
}

/** Online-first and print years often differ by one; more than that is a conflict. */
function yearAgrees(
  clues: ReferenceClues,
  candidate: TitleCandidate
): boolean | null {
  if (clues.year === null || candidate.year === null) {
    return null;
  }
  return Math.abs(clues.year - candidate.year) <= 1;
}

/** Score without a picked-out title: how much of the candidate's title the line contains. */
function containment(reference: string, candidateTitle: string): number {
  const titleTokens = tokens(candidateTitle);
  if (titleTokens.size < 3) {
    return 0;
  }
  const written = tokens(reference);
  let shared = 0;
  for (const token of titleTokens) {
    if (written.has(token)) {
      shared += 1;
    }
  }
  return shared / titleTokens.size;
}

/**
 * Whether the authors and year back up a title match. Short titles ("Sleep and
 * memory") match many unrelated works, so they need a named author; without a
 * picked-out title, both author and year must agree.
 */
function confirmsMatch(
  clues: ReferenceClues,
  author: boolean | null,
  year: boolean | null
): boolean {
  if (!clues.title) {
    return author === true && year === true;
  }
  if (tokens(clues.title).size < SHORT_TITLE_TOKENS) {
    return author === true;
  }
  return author === true || year === true;
}

function scoreCandidate(
  reference: string,
  clues: ReferenceClues,
  candidate: TitleCandidate
): TitleMatch {
  const titleScore = clues.title
    ? titleSimilarity(clues.title, candidate.title)
    : containment(reference, candidate.title);
  const author = authorNamed(reference, candidate);
  const year = yearAgrees(clues, candidate);
  const differences: TitleMatch["differences"] = [];
  if (titleScore < FOUND_THRESHOLD) {
    differences.push("title");
  }
  if (year === false) {
    differences.push("year");
  }
  if (author === false) {
    differences.push("author");
  }
  const confirmed = confirmsMatch(clues, author, year);
  let verdict: TitleVerdict = "not-found";
  if (titleScore >= FOUND_THRESHOLD && differences.length === 0 && confirmed) {
    verdict = "found";
  } else if (titleScore >= CLOSE_THRESHOLD) {
    verdict = "close";
  }
  return { verdict, candidate, titleScore, differences };
}

const VERDICT_RANK: Record<TitleVerdict, number> = {
  found: 2,
  close: 1,
  "not-found": 0,
};

/** Pick the candidate that best matches the reference and judge it. */
export function judgeCandidates(
  reference: string,
  clues: ReferenceClues,
  candidates: TitleCandidate[]
): TitleMatch {
  let best: TitleMatch | null = null;
  for (const candidate of candidates) {
    const match = scoreCandidate(reference, clues, candidate);
    if (
      !best ||
      VERDICT_RANK[match.verdict] > VERDICT_RANK[best.verdict] ||
      (VERDICT_RANK[match.verdict] === VERDICT_RANK[best.verdict] &&
        match.titleScore > best.titleScore)
    ) {
      best = match;
    }
  }
  if (
    !best ||
    (best.verdict === "not-found" && best.titleScore < NEAREST_THRESHOLD)
  ) {
    return {
      verdict: "not-found",
      candidate: null,
      titleScore: best?.titleScore ?? 0,
      differences: [],
    };
  }
  return best;
}

type SearchOutcome =
  | { ok: true; candidates: TitleCandidate[] }
  | { ok: false; reason: TitleLookupFailure };

async function search(
  url: string,
  parse: (body: unknown) => TitleCandidate[],
  fetchImpl: FetchLike,
  signal?: AbortSignal
): Promise<SearchOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(url, { signal });
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    return { ok: false, reason: "network" };
  }
  if (response.status === 429) {
    return { ok: false, reason: "rate-limited" };
  }
  if (!response.ok) {
    return { ok: false, reason: "unavailable" };
  }
  const body = await response.json().catch(() => null);
  return { ok: true, candidates: parse(body) };
}

/**
 * Search Crossref for the reference, then OpenAlex when Crossref has no strong match.
 * Fails only when neither index could be searched; a failed fallback keeps the
 * Crossref verdict.
 */
export async function lookupReferenceByTitle(
  reference: string,
  options: { fetchImpl?: FetchLike; signal?: AbortSignal } = {}
): Promise<TitleLookupResult> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const clues = extractReferenceClues(reference);
  const searched: TitleCandidate["source"][] = [];
  const candidates: TitleCandidate[] = [];
  let failure: TitleLookupFailure | null = null;

  const crossref = await search(
    crossrefSearchUrl(reference, clues),
    parseCrossrefCandidates,
    fetchImpl,
    options.signal
  );
  if (crossref.ok) {
    searched.push("Crossref");
    candidates.push(...crossref.candidates);
    const match = judgeCandidates(reference, clues, candidates);
    if (match.verdict === "found") {
      return { ok: true, searched, ...match };
    }
  } else {
    failure = crossref.reason;
  }

  const openAlex = await search(
    openAlexSearchUrl(reference, clues),
    parseOpenAlexCandidates,
    fetchImpl,
    options.signal
  );
  if (openAlex.ok) {
    searched.push("OpenAlex");
    candidates.push(...openAlex.candidates);
  } else {
    failure = failure ?? openAlex.reason;
  }

  if (searched.length === 0) {
    return { ok: false, reason: failure ?? "unavailable" };
  }
  return {
    ok: true,
    searched,
    ...judgeCandidates(reference, clues, candidates),
  };
}
